// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import { openOverlay, closeButton } from './overlay';
import { getMeta } from './data';
import { esc, num, km2, pct, longDate } from './format';
import { SCOPE, ALRA_CAVEAT } from './theme';

export function openAbout(): void {
  const meta = getMeta();
  openOverlay({
    panelClass: 'modal',
    label: 'About this site, its sources and its limitations',
    lockScroll: true,
    build(panel, close) {
      const head = document.createElement('div');
      head.className = 'overlay-head';
      const h = document.createElement('h2');
      h.textContent = 'About this site';
      head.append(h, closeButton(close, 'Close the about panel'));

      const body = document.createElement('div');
      body.className = 'stack';
      body.innerHTML = `
        <div class="note legal">
          <strong>Indicative boundaries. Not legal advice.</strong>
          The boundaries on this site come from the Tribunal’s spatial data and are indicative only.
          They are not cadastral or legal boundaries, they cannot be used to establish rights over any
          particular parcel of land or water, and nothing here is legal advice. For the authoritative
          position, read the Federal Court judgment linked from each determination, or contact the
          National Native Title Tribunal.
        </div>

        <div class="detail-block">
          <h3>What this is</h3>
          <p>Australia’s native title registers are public, and they are published as open data — but they
            are published as spatial layers for people who already have a GIS and already know the law.
            This site reads all seven of them together and tries to answer the questions a person actually
            arrives with: how much of the country is this about, what applies where, how did it get here,
            what is still being decided, and what does the register say about one particular determination.</p>
          <p class="small muted">${esc(SCOPE)}</p>
        </div>

        <div class="detail-block">
          <h3>Where the numbers come from</h3>
          <p class="small">Native title has been recognised over <strong>${esc(km2(meta.areas.recognisedKm2))}</strong>
            of land — ${esc(pct(meta.areas.recognisedPct))} of Australia’s
            ${esc(num(meta.australiaLandKm2))} km² land area as published by Geoscience Australia —
            of which ${esc(km2(meta.areas.exclusiveKm2))} (${esc(pct(meta.areas.exclusiveShareOfRecognised))} of
            the recognised area) is exclusive possession.</p>
          <p class="small">Those are <strong>dissolved unions</strong>, not sums. Where boundaries overlap,
            adding their areas counts the same ground twice. On the determination outcomes the overlap is
            small; on the agreements register it is not — its own area column sums to
            ${esc(num(meta.areas.agreementNaiveSumKm2))} km², while the ground those agreements actually
            cover is ${esc(num(meta.areas.agreementUnionKm2))} km².
            ${esc(pct(meta.areas.agreementOverlapPct, 0))} of the column is the same country counted more
            than once. Every area on this site is measured once, in an Australian Albers equal-area
            projection, after the overlaps are resolved.</p>
          <p class="small">That method is checked rather than assumed: run over the Australian Bureau of
            Statistics state boundaries, it reproduces Geoscience Australia’s published state areas to
            <strong>${esc(pct(meta.gates.projectionDriftPct, 2))}</strong>. The build fails if that ever drifts.</p>
        </div>

        <div class="detail-block">
          <h3>Things it is easy to read wrongly</h3>
          <ul class="about-list small">
            <li><strong>A determination is not automatically a recognition.</strong> 130 of the
              ${esc(num(meta.counts.determinations))} determinations found that native title does
              <em>not</em> exist in the area the court considered.</li>
            <li><strong>The Northern Territory.</strong> ${esc(ALRA_CAVEAT)}</li>
            <li><strong>Tasmania and the ACT are real zeroes.</strong> No determination has ever been made
              in either. That is a fact about the register, not missing data.</li>
            <li><strong>Sea determinations are excluded from every percentage.</strong> The denominator is
              land, so the ${esc(km2(meta.areas.recognisedSeaKm2))} of recognised sea country sits outside
              every share on this site and is shown as its own map layer.</li>
            <li><strong>“Still open” is not a queue with a finish line.</strong> These registers hold only
              live matters — determined ones have left them — so the site shows how long a matter has been
              running, and never a completed lodgement-to-determination duration, which cannot be computed
              from these files.</li>
            <li><strong>The registration test is a threshold, not a verdict.</strong> An application that has
              not passed it may be amended and tested again; it says nothing about the merits.</li>
            <li><strong>Blank is not “no”.</strong> Several agreement fields are simply not recorded on a
              quarter of the register. They are shown as their own state and never counted as a no.</li>
          </ul>
        </div>

        <div class="detail-block">
          <h3>What this site will not do</h3>
          <p class="small">It does not rank Aboriginal and Torres Strait Islander groups against one
            another, and it has no leaderboard of holders, determinations or country. It does not describe
            outcomes as wins or losses: a finding that native title does not exist over an area is a legal
            conclusion about that ground on the evidence before the court, and this site says so in those
            words. Names of native title holders and of bodies corporate are reproduced exactly as the
            register states them, never shortened to fit a layout. The applicant field on the agreements
            register names private individuals and is not downloaded at all.</p>
        </div>

        <div class="detail-block">
          <h3>The registers</h3>
          <table class="small">
            <thead><tr><th>Register</th><th class="num">Rows</th><th>Extracted</th></tr></thead>
            <tbody>${meta.sources.map((s) => `<tr>
              <td><a href="${esc(s.dataset)}" target="_blank" rel="noopener">${esc(s.label)}</a></td>
              <td class="num">${esc(num(sourceCount(s.key)))}</td>
              <td>${esc(longDate(s.extracted))}</td>
            </tr>`).join('')}</tbody>
          </table>
          <p class="xsmall muted caveat">Data currency is per register, not per site — the Tribunal refreshes
            each one on its own schedule. Everything is © Commonwealth of Australia (National Native Title
            Tribunal), reused under CC BY 4.0. This site is updated monthly and was last built
            ${esc(longDate(meta.builtAt.slice(0, 10)))}.</p>
        </div>
      `;

      panel.append(head, body);
    },
  });

  function sourceCount(key: string): number {
    const c = getMeta().counts;
    switch (key) {
      case 'determinations': return c.determinations;
      case 'outcomes': return c.outcomePolygons;
      case 'claims': return c.registeredClaims;
      case 'schedule': return c.scheduledApplications;
      case 'agreements': return c.agreements;
      case 'bodies': return c.bodiesCorporate;
      case 'ratsib': return c.representativeRegions;
      default: return 0;
    }
  }
}
